import { Injectable } from '@angular/core';
import { SigninWithCredentialsOptions, UserClaims } from '@okta/okta-auth-js';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  catchError,
  filter,
  firstValueFrom,
  switchMap,
  tap,
} from 'rxjs';

import { DoceboToken } from '@app/models/docebo-token.model';
import { DoceboService } from './docebo/docebo.service';
import { OktaService } from './okta/okta.service';
import { ApiService } from '@app/services/apiService.service';
import { GenesysAuthService } from '@app/services/genesysAuthService.service';

/**
 * Service responsible for coordinating authentication across Okta and Docebo.
 * This service wraps OktaService and DoceboService to provide a unified auth interface.
 *
 * Okta silent login (cross-app SSO) is handled automatically by OktaService on app startup.
 * Once Okta authentication succeeds and user claims are available, this service automatically
 * resolves the Docebo session using the user's preferred_username.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _isAuthenticated$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private apiService: ApiService,
    private doceboService: DoceboService,
    private oktaService: OktaService,
    private genesysAuthService: GenesysAuthService
  ) {
    this.loadUserData();
  }

  async loadUserData(): Promise<void> {
    // Automatically resolve the Docebo session once Okta claims become available.
    // This replaces the manual loadExistingSession() call — it fires reactively
    // whenever Okta authentication succeeds (including silent login).
    const auth = await firstValueFrom(this.oktaService.isAuthenticated$);
    if (!auth) {
      this._isAuthenticated$.next(false);
    } else {
      this.oktaService.claims$
        .pipe(
          filter((claims) => !!claims?.preferred_username),
          switchMap((claims) => this.doceboService.setAccessToken(claims.preferred_username)),
          switchMap(() => this.getDoceboAccessToken()),
          tap((doceboAuthToken: DoceboToken) => {
            if (doceboAuthToken && doceboAuthToken.access_token) {
              this.apiService.setToken(doceboAuthToken.access_token);
              console.debug('[Services][AuthService] Docebo session resolved from Okta claims.');
              this._isAuthenticated$.next(true);
            }
          }),
          catchError((error) => {
            console.error('[Services][AuthService] Error resolving Docebo session:', error);
            this._isAuthenticated$.next(false);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  /**
   * Return Okta User Claims Observable
   * @returns Okta user claims, for example first/last name and email
   */
  get claims$(): Observable<UserClaims> {
    return this.oktaService.claims$;
  }

  /**
   * Return Observable boolean if user is authenticated with Okta
   * @returns true when authenticated with Okta
   */
  get isAuthenticated$(): Observable<boolean> {
    return this._isAuthenticated$;
  }

  /**
   * Logout of Okta and Docebo, clear tokens, and redirect.
   */

  /**
   * TODO: Refactor
   * Logout of okta and remove _docebo token from local storage
   */
  logoutAndRedirectTo(redirectUrl): void {
    this.logoutDocebo();
    this.logoutBeyond();
    this.apiService.removeToken();
    this.genesysAuthService.logoutOfGenesys();
    this.signOut(); // This must occur after all other logouts, as it redirects the page and nothing else can happen after it.
  }

  /**
   * Return Okta access token
   * @returns - Okta access token
   */
  getOktaAccessToken(): string {
    return this.oktaService.getAccessToken();
  }

  /**
   * Return docebo Token Observable
   * @returns - Observable with DoceboToken
   */
  getDoceboAccessToken(): Observable<DoceboToken> {
    return this.doceboService.accessToken$;
  }

  /**
   * Get specific information about currently Docebo logged user.
   * TODO: Add Docebo User Interface
   * @param userId - Docebo user id
   * @returns Observable Docebo User Session object
   */
  getDoceboUser(userId: string): Observable<any> {
    return this.doceboService.getUser(userId);
  }

  /**
   * Logout user from Docebo
   */
  private logoutDocebo(): void {
    this.doceboService.removeDoceboToken();
  }

  /**
   * TODO: Refactor
   * Log user our from the Beyond app
   */
  private logoutBeyond(): void {
    this.oktaService.clearTokens();
  }

  /**
   * Authenticate the user with custom Okta login page
   * @param options has username and password
   * @returns Okta AuthTransaction promise
   */
  async signInWithCredentials(options: SigninWithCredentialsOptions): Promise<void> {
    return this.oktaService.signInWithCredentials(options);
  }

  /**
   * Sign In with Okta Hosted Login
   * @param originalUri string uri path
   */
  signInWithRedirect(originalUrl: string): Promise<void> {
    return this.oktaService.signInWithRedirect(originalUrl);
  }

  /**
   * Sign Out of Okta with redirect url
   */
  signOut(): void {
    this.oktaService.signOut();
  }
}
